import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  prefersReducedMotion,
  debounce,
  isLowPowerDevice,
  isClickedOutside,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLButtonElement} [previousButton] - The previous media button.
 * @property {HTMLButtonElement} [nextButton] - The next media button.
 * @property {HTMLElement} [currentIndexDisplay] - The current index counter display.
 * @property {HTMLElement} [totalCountDisplay] - The counter's total display (navigable count when the video filter is active).
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  // The current index, tracked explicitly rather than derived from
  // aria-selected - that attribute is written by both selectThumbnail and
  // the scroll-driven handleScroll, and reading it back as "current index"
  // mid-scroll is what caused arrow-key navigation to skip entries.
  #currentIndex = 0;

  // True while a selectThumbnail-initiated scroll is in flight, so
  // handleScroll's IntersectionObserver-driven sync doesn't race with and
  // overwrite the index that was just explicitly set.
  #programmaticScroll = false;

  // The element that had focus when the dialog opened, so focus can be
  // returned to it on close (WCAG 2.4.3). Captured in open() before
  // showModal(); restored in closeDialog().
  #lastFocusedElement = /** @type {HTMLElement | null} */ (null);

  // The most recently autoplayed video media container, so it can be paused
  // when the active item changes instead of playing on behind the panel.
  #activeVideoContainer = /** @type {HTMLElement | null} */ (null);

  // Bumped by every open/close transition. Starting a view transition skips
  // the one in flight, so a close clicked mid-open (or an open mid-close)
  // would otherwise have the interrupted transition's cleanup strip the
  // view-transition names the new one just set.
  #transitionToken = 0;

  // True while a close transition is running, so repeat clicks or Escape
  // presses don't restart it.
  #closing = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll, { passive: true });
    this.refs.dialog.addEventListener('keydown', this.handleKeyDown);
    this.refs.dialog.addEventListener('click', this.handleDialogClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('keydown', this.handleKeyDown);
    this.refs.dialog.removeEventListener('click', this.handleDialogClick);
  }

  /**
   * Light-dismiss: clicking the blurred backdrop (outside the panel) closes
   * the dialog. Clicks on the panel or any of its children fall through to
   * their own handlers, so this never double-closes on an image click.
   */
  handleDialogClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) {
      this.close();
    }
  };

  /**
   * Opens the zoom dialog.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    // Remember what opened the dialog so focus can return there on close.
    this.#lastFocusedElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Opening from a video should only let you browse between the
    // product's other videos, not its photos - and vice versa. Must be set
    // BEFORE #updateIndex below: the counter and prev/next disabled state
    // are derived from this filter attribute.
    const isVideo = targetImage?.matches(
      '.product-media-container--video, .product-media-container--external_video'
    );
    this.toggleAttribute('data-media-filter-video', !!isVideo);
    this.toggleAttribute('data-reduce-effects', isLowPowerDevice());

    // Set the initial selection state synchronously so the first arrow
    // keypress has an accurate starting index, instead of waiting on the
    // ~50ms-debounced scroll-driven IntersectionObserver sync.
    this.#updateIndex(index);

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }

      this.#trackActiveVideo(targetImage);

      // Start the high-res fetch now so the swap happens under cover of the
      // open animation rather than a beat after it - and so it fires on the
      // low-power / no-transition path too (which otherwise never loads it).
      // Idempotent via #highResImagesLoaded, so the later selectThumbnail
      // call is a no-op.
      this.loadHighResolutionImage(targetImage);

      // ✅ FIX: move keyboard focus INTO zoom dialog
      // Prefer the thumbnail we're opening on - querying the dialog blindly
      // can land on an element that's hidden (e.g. a photo's zoom button
      // when the dialog is filtered to videos-only), and focusing a
      // display:none element silently no-ops.
      focusZoomDialog(dialog, targetThumbnail ?? undefined);
    };

    /** @type {HTMLElement | null} */
    const sourceImage =
      event.target instanceof Element
        ? event.target.closest('li,slideshow-slide')
        : null;

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      prefersReducedMotion() ||
      !sourceImage ||
      !targetImage
    )
      return open();

    const itemTransitionName = `gallery-item-open`;
    // Morph from a stand-in over the gallery slide rather than the slide
    // itself: a named element drops out of the page while the transition
    // runs, so naming the slide left a hole that popped back afterwards.
    const ghost = createTransitionGhost(sourceImage, itemTransitionName);
    const token = ++this.#transitionToken;

    // While a view transition runs, its overlay takes every click (they land
    // on <html>), so a quick click to dismiss would be swallowed until the
    // open animation finished. Route those clicks to the control under the
    // pointer - prev/next or a thumbnail - and treat anything else (photo,
    // scrim, close button) as close, same as when the dialog is at rest.
    const handleEarlyClick = (/** @type {MouseEvent} */ clickEvent) => {
      if (clickEvent.target !== document.documentElement || !dialog.open) return;

      const { clientX, clientY } = clickEvent;
      const control = Array.from(
        dialog.querySelectorAll('.dialog-zoomed-gallery__nav-button, .dialog-thumbnails-list__thumbnail')
      ).find((element) => {
        const { top, right, bottom, left } = element.getBoundingClientRect();
        return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
      });

      if (control instanceof HTMLElement) {
        control.click();
      } else {
        this.close();
      }
    };
    document.addEventListener('click', handleEarlyClick);

    // Fade the scrim in as part of the transition (page snapshot cross-fade)
    // rather than with its own CSS animation, which starts at showModal()
    // and has mostly run by the time the snapshots are taken and the first
    // frame paints - so the scrim popped in. Same clock as the image morph.
    const root = document.documentElement;
    root.style.setProperty('view-transition-name', 'zoom-dialog-root');
    this.setAttribute('data-view-transition', '');

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    try {
      await startViewTransition(() => {
        open();
        ghost.remove();
        targetImage.style.setProperty('view-transition-name', itemTransitionName);
      });
    } finally {
      document.removeEventListener('click', handleEarlyClick);
    }

    // Interrupted by a close: that transition owns the names now.
    if (token !== this.#transitionToken) return;

    root.style.removeProperty('view-transition-name');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Single source of truth for "the active media item changed": updates
   * the tracked index, thumbnail aria-selected state, the visible counter,
   * and the prev/next button disabled state. Previously this was
   * duplicated across open(), handleScroll, and selectThumbnail.
   */
  #updateIndex(index) {
    const { thumbnails, previousButton, nextButton, currentIndexDisplay, totalCountDisplay, statusDisplay } =
      this.refs;

    // aria-current is valid on any element; aria-selected is only meaningful
    // inside a listbox/tab/grid, which these plain buttons are not.
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      if (i === index) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
    this.#currentIndex = index;

    // When the video/photo filter is active, the counter and announcements
    // reflect the NAVIGABLE set ("1 / 2" for the second of two videos), not
    // the raw media list - otherwise a filtered gallery reads "9 / 11" while
    // only two items are reachable.
    const indices = this.#navigableIndices();
    const position = indices.indexOf(index);
    const displayIndex = position === -1 ? index : position;
    const displayTotal = position === -1 ? thumbnails.children.length : indices.length;

    if (currentIndexDisplay) {
      currentIndexDisplay.textContent = `${displayIndex + 1}`;
    }

    if (totalCountDisplay) {
      totalCountDisplay.textContent = `${displayTotal}`;
    }

    // Announce phrased position ("Slide N of M") to screen readers via the
    // visually-hidden live region, rather than the bare ordinal.
    if (statusDisplay instanceof HTMLElement) {
      const template = statusDisplay.dataset.statusTemplate;
      if (template) {
        statusDisplay.textContent = template
          .replace('%INDEX%', `${displayIndex + 1}`)
          .replace('%LENGTH%', `${displayTotal}`);
      }
    }

    // Read focus first: Chromium drops it the moment a focused button is disabled.
    const active = document.activeElement;

    // Disable prev/next at the bounds of the NAVIGABLE set (which excludes
    // slides hidden by the video/photo filter), not the raw thumbnail list.
    if (previousButton instanceof HTMLButtonElement) {
      previousButton.disabled = position === -1 ? index <= 0 : position <= 0;
    }

    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled =
        position === -1
          ? index >= thumbnails.children.length - 1
          : position >= indices.length - 1;
    }

    // A button that just became disabled can't hold focus; hand it to its counterpart so keyboard users aren't dropped.
    if (active === nextButton && nextButton?.disabled && previousButton instanceof HTMLButtonElement) {
      previousButton.focus();
    } else if (active === previousButton && previousButton?.disabled && nextButton instanceof HTMLButtonElement) {
      nextButton.focus();
    }
  }

  /**
   * The raw thumbnail indices that are currently navigable. When the dialog
   * was opened from a video, the data-media-filter-video attribute hides all
   * non-video slides/thumbnails (CSS display:none) - navigation must skip
   * those, or prev/next lands on an invisible slide. Derived from the same
   * data-media-type attributes the filter CSS matches, so it stays correct
   * even while the dialog itself is closed (when visibility checks would
   * report everything hidden).
   */
  #navigableIndices() {
    const buttons = Array.from(this.refs.thumbnails.querySelectorAll('button'));
    const videosOnly = this.hasAttribute('data-media-filter-video');

    const indices = [];
    buttons.forEach((button, index) => {
      const type = button.getAttribute('data-media-type');
      if (!videosOnly || type === 'video' || type === 'external_video') {
        indices.push(index);
      }
    });
    return indices;
  }

  /**
   * Steps to the previous/next navigable media item, clamped to the gallery
   * bounds and skipping any slides hidden by the video/photo filter.
   * Shared by the prev/next nav buttons and ArrowLeft/ArrowRight.
   */
  #step(direction) {
    const indices = this.#navigableIndices();
    if (!indices.length) return;

    const position = indices.indexOf(this.#currentIndex);
    const nextPosition =
      position === -1
        ? direction > 0
          ? 0
          : indices.length - 1
        : Math.min(Math.max(position + direction, 0), indices.length - 1);

    const nextIndex = indices[nextPosition];
    if (nextIndex === this.#currentIndex) return;

    this.selectThumbnail(nextIndex, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * Navigates to the previous media item. Wired to the visible prev button.
   */
  previous() {
    this.#step(-1);
  }

  /**
   * Navigates to the next media item. Wired to the visible next button.
   */
  next() {
    this.#step(1);
  }

  /**
   * Tracks the video media item that is the dialog's current target so it can
   * be paused when the user navigates away. Videos are NOT auto-played: the
   * target shows its poster with a play-button overlay and only loads/plays
   * when the user clicks it, so there is no silent looping "preview". Any
   * previously-active video is paused first so it doesn't keep playing
   * (audio + motion) off-screen behind the panel.
   */
  #trackActiveVideo(targetImage) {
    if (targetImage !== this.#activeVideoContainer) {
      this.#pauseActiveVideo();
    }

    const deferredMedia = targetImage?.querySelector('deferred-media');
    if (!deferredMedia) return;

    this.#activeVideoContainer = targetImage;
  }

  /**
   * Pauses the most recently autoplayed video/iframe, if any.
   */
  #pauseActiveVideo() {
    const container = this.#activeVideoContainer;
    this.#activeVideoContainer = null;
    if (!container) return;

    const video = container.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      video.pause();
      return;
    }

    // External (YouTube/Vimeo) embeds expose no direct pause API here;
    // reloading the iframe stops playback. Reload with autoplay disabled so it
    // doesn't immediately restart and keep playing (with sound) off-screen.
    // Shopify's external_video_url may serialize the flag as autoplay=1 or
    // autoplay=true - handle both. Only reload when it would actually change
    // something: assigning src always reloads, even when identical.
    const iframe = container.querySelector('iframe');
    if (iframe instanceof HTMLIFrameElement && /autoplay=(1|true)\b/.test(iframe.src)) {
      iframe.src = iframe.src.replace(/autoplay=(1|true)\b/, 'autoplay=0');
    }
  }

  /**
   * Loads a high-resolution image for a specific media container
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image'))
      return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl))
      return false;

    // newImage below already fetches the URL; a separate preload here would
    // double-request it. Hover prefetch also routes through this method, so
    // the same single fetch warms the cache.
    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Scroll handler
   */
  handleScroll = debounce(async () => {
    if (this.#programmaticScroll) return;

    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);

    const targetThumbnail = thumbnails.children[activeIndex];
    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#updateIndex(activeIndex);

    // Track videos reached by swipe/scroll too - otherwise a video played on
    // a scrolled-to slide is never registered, and navigating away (or
    // closing) would pause a stale slide while it keeps playing with audio.
    this.#trackActiveVideo(mostVisibleElement);

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * CLOSE (transition version)
   */
  async close() {
    const { dialog, media } = this.refs;

    if (this.#closing || !dialog.open) return;

    if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion()) {
      return this.closeDialog();
    }

    // Use the synchronously-tracked index rather than re-deriving the visible
    // slide with a per-call IntersectionObserver (which could snapshot
    // mid-scroll). At rest #currentIndex is the visible slide.
    const activeIndex = this.#currentIndex;
    const mostVisibleElement = media[activeIndex];
    if (!mostVisibleElement) return this.closeDialog();

    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    // In grid mode, videos are rendered in their own row, separate from the
    // photo grid <ul> - so the gallery's combined media[] refs are ordered
    // photos-then-videos, not in the original product media order. Match by
    // media id instead of index to find the correct transition source.
    const mediaId = mostVisibleElement.dataset.mediaId;
    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[activeIndex]
      : mediaGallery?.media?.find((element) => element.dataset.mediaId === mediaId) ??
        mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    // Start the morph right away rather than waiting out the chrome's exit
    // animations (and, right after opening, the thumbnail strip's delayed
    // slide-in) - the chrome fades with the dialog's snapshot instead.
    dialog.classList.add('dialog--closed');
    this.#closing = true;
    const token = ++this.#transitionToken;

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    // Capture the page - dark scrim included - as the root snapshot for this
    // transition only, so the scrim fades out alongside the image morph
    // instead of vanishing the instant dialog.close() removes it.
    const root = document.documentElement;
    root.style.setProperty('view-transition-name', 'zoom-dialog-root');

    // Land on a stand-in over the slide, not the slide itself, so the
    // gallery photo stays put under the fading scrim instead of dropping
    // out for the morph and popping back in at the end.
    let ghost = null;
    try {
      await startViewTransition(() => {
        mostVisibleElement.style.removeProperty('view-transition-name');
        this.closeDialog();
        ghost = createTransitionGhost(slide, itemTransitionName);
      });
    } finally {
      this.#closing = false;
      ghost?.remove();
      dialog.classList.remove('dialog--closed');
    }

    // Interrupted by a reopen: that transition owns the names now.
    if (token !== this.#transitionToken) return;

    root.style.removeProperty('view-transition-name');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  /**
   * BASIC CLOSE
   */
  closeDialog() {
    const { dialog } = this.refs;

    // Stop any video that was playing so it doesn't keep running after close.
    this.#pauseActiveVideo();

    dialog.close();
    // Re-arm the scrim's CSS fade-in (suppressed while an open transition
    // fades it instead) only once the dialog - and its scrim - are gone, so
    // it can't replay over an already-dark scrim.
    this.removeAttribute('data-view-transition');
    window.dispatchEvent(new DialogCloseEvent());

    // Return focus to whatever opened the dialog (the maximize button),
    // since native dialog.close() otherwise drops focus to <body>.
    this.#lastFocusedElement?.focus?.();
    this.#lastFocusedElement = null;
  }

  /**
   * Keydown handler for the dialog
   */
  handleKeyDown = (event) => {
    const { dialog } = this.refs;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    // Focus trap: showModal() makes the page inert but does NOT contain
    // sequential Tab focus inside the dialog, so wrap it manually. Recompute
    // the focusable set each time - the video/photo filter hides (display:none)
    // a different set of thumbnails per open, and getFocusableElements drops
    // those hidden/disabled controls so first/last stay actually focusable.
    if (event.key === 'Tab') {
      const focusable = getFocusableElements(dialog);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (!dialog.open) return;

    // Home/End jump to the first/last navigable media item (respecting the
    // video/photo filter), matching the arrow keys' filter-aware stepping.
    if (event.key === 'Home' || event.key === 'End') {
      const indices = this.#navigableIndices();
      if (!indices.length) return;

      event.preventDefault();
      const targetIndex = event.key === 'Home' ? indices[0] : indices[indices.length - 1];
      if (targetIndex !== this.#currentIndex) {
        this.selectThumbnail(targetIndex, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
      }
      return;
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    this.#step(event.key === 'ArrowRight' ? 1 : -1);
  };

  /**
   * Thumbnail click
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Hover preload
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Select thumbnail
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails?.children.length) return;

    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length)
      return;

    const { dialog, media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#updateIndex(index);

    if (options.behavior === 'smooth') {
      this.#programmaticScroll = true;
      const stopSuppressing = () => {
        this.#programmaticScroll = false;
      };
      dialog.addEventListener('scrollend', stopSuppressing, { once: true });
      setTimeout(stopSuppressing, 600);
    }

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({ behavior: options.behavior });
      this.loadHighResolutionImage(targetImage);
      this.#trackActiveVideo(targetImage);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Focus helper (ADA fix)
 */
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * The dialog's focusable controls, excluding hidden (display:none, e.g.
 * filtered thumbnails) and disabled ones, in DOM order.
 */
function getFocusableElements(dialog) {
  return /** @type {HTMLElement[]} */ (
    Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) =>
        el instanceof HTMLElement &&
        !el.hasAttribute('disabled') &&
        el.getClientRects().length > 0
    )
  );
}

function focusZoomDialog(dialog, preferredTarget) {
  if (!dialog) return;

  // Only ever land on an actually-focusable control - the previous selector
  // included bare `img`/`a`, which can match a non-interactive element whose
  // focus() silently no-ops (e.g. single-media galleries render no thumbnail
  // buttons). Fall back to focusing the dialog itself.
  const firstFocusable = preferredTarget ?? getFocusableElements(dialog)[0];

  if (firstFocusable instanceof HTMLElement) {
    firstFocusable.focus();
  } else {
    dialog.setAttribute('tabindex', '-1');
    dialog.focus();
  }
}

/**
 * An invisible box fixed over `element` that carries the view-transition
 * name in its place, so the morph tracks the element's position without
 * pulling the element itself out of the page while the transition runs.
 */
function createTransitionGhost(element, name) {
  const { top, left, width, height } = element.getBoundingClientRect();
  const ghost = document.createElement('div');
  ghost.setAttribute('aria-hidden', 'true');
  Object.assign(ghost.style, {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
    height: `${height}px`,
    pointerEvents: 'none',
    viewTransitionName: name,
  });
  document.body.append(ghost);
  return ghost;
}

/**
 * Get most visible element
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );

        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}